import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { projectAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProject, setEditingProject] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    images: [''],
    service_category: '',
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin/login');
    } else {
      fetchProjects();
    }
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      const response = await projectAPI.getAll();
      setProjects(response.data);
    } catch {
      toast.error('Projeler yüklenemedi');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      images: [''],
      service_category: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await projectAPI.update(editingProject.id, formData);
        toast.success('Proje güncellendi');
      } else {
        await projectAPI.create(formData);
        toast.success('Proje oluşturuldu');
      }

      setShowDialog(false);
      setEditingProject(null);
      resetForm();
      fetchProjects();
    } catch {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Silmek istediğinizden emin misiniz?')) return;

    try {
      await projectAPI.delete(id);
      toast.success('Proje silindi');
      fetchProjects();
    } catch {
      toast.error('Silinemedi');
    }
  };

  const openEdit = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      images: project.images || [''],
      service_category: project.service_category,
    });
    setShowDialog(true);
  };

  return (
    <div className="min-h-screen bg-light" data-testid="admin-projects-page">

      {/* NAVBAR */}
      <nav className="bg-navy text-white p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link
            to="/admin/dashboard"
            className="flex items-center space-x-2 hover:text-orange"
          >
            <ArrowLeft size={20} />
            <span>Dashboard'a Dön</span>
          </Link>

          <h1 className="text-2xl font-bold">İşlerimiz Yönetimi</h1>
        </div>
      </nav>

      {/* CONTENT */}
      <div className="max-w-7xl mx-auto px-6 py-12">

        <Button
          onClick={() => {
            setEditingProject(null);
            resetForm();
            setShowDialog(true);
          }}
          className="mb-6 bg-orange hover:bg-orange/90"
        >
          <Plus className="mr-2" size={20} />
          Yeni Proje Ekle
        </Button>

        {/* PROJECT LIST */}
        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project) => (
            <div
              key={project.id}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              {/* IMAGE PREVIEW */}
              <img
                src={project.images?.[0]}
                alt={project.title}
                className="h-40 w-full object-cover"
              />

              <div className="p-4">
                <h3 className="font-bold text-navy">
                  {project.title}
                </h3>

                <p className="text-sm text-slate-600">
                  {project.description}
                </p>

                <div className="flex justify-end space-x-2 mt-4">

                  <button
                    onClick={() => openEdit(project)}
                    className="p-2 text-navy hover:bg-slate-100 rounded"
                  >
                    <Pencil size={18} />
                  </button>

                  <button
                    onClick={() => handleDelete(project.id)}
                    className="p-2 text-red-600 hover:bg-slate-100 rounded"
                  >
                    <Trash2 size={18} />
                  </button>

                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* DIALOG */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">

          <DialogHeader>
            <DialogTitle>
              {editingProject ? 'Proje Düzenle' : 'Yeni Proje'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* TITLE */}
            <div>
              <label className="block text-sm font-semibold mb-1">
                Başlık
              </label>
              <input
                required
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    title: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            {/* DESCRIPTION */}
            <div>
              <label className="block text-sm font-semibold mb-1">
                Açıklama
              </label>
              <textarea
                required
                rows={3}
                value={formData.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>

            {/* MULTI IMAGE */}
            <div>
              <label className="block text-sm font-semibold mb-2">
                Görseller
              </label>

              {formData.images.map((img, index) => (
                <div key={index} className="flex gap-2 mb-2">

                  <input
                    required
                    type="url"
                    value={img}
                    onChange={(e) => {
                      const newImages = [...formData.images];
                      newImages[index] = e.target.value;
                      setFormData({
                        ...formData,
                        images: newImages,
                      });
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Görsel URL"
                  />

                  <button
                    type="button"
                    onClick={() => {
                      const newImages =
                        formData.images.filter(
                          (_, i) => i !== index
                        );

                      setFormData({
                        ...formData,
                        images: newImages.length
                          ? newImages
                          : [''],
                      });
                    }}
                    className="px-3 bg-red-500 text-white rounded-lg"
                  >
                    Sil
                  </button>

                </div>
              ))}

              <button
                type="button"
                onClick={() =>
                  setFormData({
                    ...formData,
                    images: [
                      ...formData.images,
                      '',
                    ],
                  })
                }
                className="mt-2 px-4 py-2 bg-navy text-white rounded-lg"
              >
                + Görsel Ekle
              </button>
            </div>

            {/* CATEGORY */}
            <div>
              <label className="block text-sm font-semibold mb-1">
                Kategori
              </label>

              <select
                required
                value={formData.service_category}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    service_category:
                      e.target.value,
                  })
                }
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Seçiniz</option>
                <option value="klima">
                  Klima
                </option>
                <option value="kombi">
                  Kombi
                </option>
                <option value="beyaz-esya">
                  Beyaz Eşya
                </option>
              </select>
            </div>

            {/* SUBMIT */}
            <Button
              type="submit"
              className="w-full bg-orange hover:bg-orange/90"
            >
              Kaydet
            </Button>

          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProjects;
